import * as React from "react";
import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useMatchRoute, useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Package, Layers, Truck, ClipboardList, LogOut, Menu, X, LayoutDashboard, Building2, MapPin, Mail, Settings } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

const navItems = [
  { label: "Pregled", to: "/admin/dashboard", icon: LayoutDashboard },
  { label: "Narudžbe", to: "/admin/orders", icon: ClipboardList },
  { label: "Proizvodi", to: "/admin/products", icon: Package },
  { label: "Lokacije", to: "/admin/lokacije", icon: MapPin },
  { label: "Kategorije", to: "/admin/categories", icon: Layers },
  { label: "Dostava", to: "/admin/shipping", icon: Truck },
  { label: "Kurirske službe", to: "/admin/shipping-credentials", icon: Building2 },
  { label: "Email", to: "/admin/emails", icon: Mail },
  { label: "Postavke", to: "/admin/settings", icon: Settings },
] as const;

export default function AdminLayout() {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => apiFetch<{ authenticated: boolean }>("/api/auth/me"),
    retry: false,
  });

  const authenticated = data?.authenticated ?? false;

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (!authenticated) {
      navigate({ to: "/admin/login" });
    } else if (location.pathname === "/admin") {
      navigate({ to: "/admin/dashboard" });
    }
  }, [isLoading, isFetching, authenticated, navigate, location.pathname]);

  if (isLoading || !authenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="text-sm text-muted-foreground">Učitavanje...</span>
      </div>
    );
  }

  async function handleLogout() {
    await apiFetch("/api/auth/logout", { method: "POST" });
    navigate({ to: "/admin/login" });
  }

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      {/* Mobile header */}
      <header className="md:hidden flex items-center justify-between px-4 h-14 border-b border-border/60 bg-white sticky top-0 z-40">
        <Link to="/admin/dashboard" className="font-semibold text-sm">
          sosline admin
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 -mr-2 text-foreground/70"
        >
          {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </header>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-b border-border/60 bg-white px-4 py-2 space-y-1">
          {navItems.map(({ label, to, icon: Icon }) => {
            const isActive = !!matchRoute({ to, fuzzy: true });
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                  isActive ? "bg-foreground text-white" : "text-foreground/70 hover:bg-muted",
                )}
              >
                <Icon className="size-4" />
                {label}
              </Link>
            );
          })}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-foreground/50 hover:text-foreground/70 w-full rounded-lg transition-colors"
          >
            <LogOut className="size-4" />
            Odjava
          </button>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-56 border-r border-border/60 bg-white p-4 flex-col shrink-0">
        <Link to="/admin/dashboard" className="font-semibold text-sm mb-6 block px-2">
          sosline admin
        </Link>

        <nav className="flex-1 space-y-1">
          {navItems.map(({ label, to, icon: Icon }) => {
            const isActive = !!matchRoute({ to, fuzzy: true });
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                  isActive ? "bg-foreground text-white" : "text-foreground/70 hover:bg-muted",
                )}
              >
                <Icon className="size-4" />
                {label}
              </Link>
            );
          })}
        </nav>

        <button
          onClick={handleLogout}
          className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-foreground/50 hover:text-foreground/70 w-full rounded-lg transition-colors"
        >
          <LogOut className="size-4" />
          Odjava
        </button>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-4 sm:p-6 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
